import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, X, Info } from 'lucide-react';
import { ToastMessage } from '../../types';

interface ToastNotificationProps {
  toasts: ToastMessage[];
  removeToast: (id: string) => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ toasts, removeToast }) => {
  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 pointer-events-none">
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} removeToast={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
};

interface ToastItemProps {
  toast: ToastMessage;
  removeToast: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, removeToast }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      removeToast(toast.id);
    }, 4000);
    return () => clearTimeout(timer);
  }, [toast.id, removeToast]);

  const colors = {
    success: 'bg-slate-900 border-emerald-500/50 text-emerald-500',
    error: 'bg-slate-900 border-red-500/50 text-red-500',
    info: 'bg-slate-900 border-blue-500/50 text-blue-500'
  };

  const icons = {
    success: CheckCircle2,
    error: AlertCircle,
    info: Info
  };

  const Icon = icons[toast.type];

  // CASTING FIX: Força o TypeScript a aceitar props no motion.div
  const MotionDiv = motion.div as any;

  return (
    <MotionDiv
      layout
      initial={{ opacity: 0, x: 50, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20, scale: 0.9 }}
      className={`pointer-events-auto min-w-[320px] max-w-sm rounded-xl border p-4 shadow-2xl backdrop-blur-md flex items-start gap-3 ${colors[toast.type]}`}
    >
      <Icon className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="flex-1">
        <h4 className="text-sm font-bold text-white">{toast.title}</h4>
        <p className="text-xs text-slate-400 mt-1">{toast.message}</p>
      </div>
      <button 
        onClick={() => removeToast(toast.id)}
        className="text-slate-500 hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </MotionDiv>
  );
};

export default ToastNotification;